/**
 * Leviathan Web - Dashboard Grid
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Draggable/resizable widget grid using react-grid-layout
 */

import { useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import { useDashboardStore, type LayoutItem } from '@/stores/dashboardStore';

interface RGLLayout {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  [key: string]: any;
}
type RGLLayouts = Record<string, RGLLayout[]>;
import { GripVertical } from 'lucide-react';

import TodaysDispatchesWidget from './TodaysDispatchesWidget';
import MonthlyRevenueWidget from './MonthlyRevenueWidget';
import ActiveDeliveriesWidget from './ActiveDeliveriesWidget';
import PastDueInvoicesWidget from './PastDueInvoicesWidget';
import LowTankAlertsWidget from './LowTankAlertsWidget';
import CompletedTodayWidget from './CompletedTodayWidget';
import QuickActionsWidget from './QuickActionsWidget';
import RecentActivityWidget from './RecentActivityWidget';

const ResponsiveGridLayout = WidthProvider(Responsive);

const WIDGET_COMPONENTS: Record<string, React.FC> = {
  'todays-dispatches': TodaysDispatchesWidget,
  'monthly-revenue': MonthlyRevenueWidget,
  'active-deliveries': ActiveDeliveriesWidget,
  'past-due-invoices': PastDueInvoicesWidget,
  'low-tank-alerts': LowTankAlertsWidget,
  'completed-today': CompletedTodayWidget,
  'quick-actions': QuickActionsWidget,
  'recent-activity': RecentActivityWidget,
};

const WIDGET_TITLES: Record<string, string> = {
  'todays-dispatches': "Today's Dispatches",
  'monthly-revenue': 'Monthly Revenue',
  'active-deliveries': 'Active Deliveries',
  'past-due-invoices': 'Past Due Invoices',
  'low-tank-alerts': 'Low Tank Alerts',
  'completed-today': 'Completed Today',
  'quick-actions': 'Quick Actions',
  'recent-activity': 'Recent Activity',
};

const DashboardGrid = () => {
  const { layout, widgets, isEditing, setLayout } = useDashboardStore();

  const visibleWidgets = useMemo(() => widgets.filter((w) => w.visible), [widgets]);

  const visibleLayout = useMemo(
    () => layout.filter((l) => visibleWidgets.some((w) => w.id === l.i)) as unknown as RGLLayout[],
    [layout, visibleWidgets]
  );

  // ONE breakpoint, ONE column count. Positions are the user's, and they hold
  // at every viewport width.
  //
  // Previously: breakpoints lg/md/sm at 1200/768/0 with 12/8/4 columns. Below
  // 1200px react-grid-layout switched to the 8-column layout and reflowed
  // everything — a widget at x=9,w=3 has nowhere to go in 8 columns, so it
  // wrapped and the carefully arranged board rearranged itself.
  const layouts: RGLLayouts = useMemo(() => ({ lg: visibleLayout }), [visibleLayout]);

  const handleLayoutChange = (
    _currentLayout: readonly RGLLayout[],
    allLayouts: Partial<Record<string, readonly RGLLayout[]>>
  ) => {
    const lgLayout = allLayouts.lg ?? _currentLayout;
    if (lgLayout) {
      const updatedLayout: LayoutItem[] = layout.map((item) => {
        const updated = lgLayout.find((l: RGLLayout) => l.i === item.i);
        return updated
          ? { i: item.i, x: updated.x, y: updated.y, w: updated.w, h: updated.h }
          : item;
      });
      setLayout(updatedLayout);
    }
  };

  return (
    // Below the grid's natural width the board scrolls sideways rather than
    // reflowing. Squeezing 12 columns into a phone would technically "hold
    // positions" but leave 25px-wide widgets; scrolling keeps them readable and
    // keeps the arrangement recognisably the one the user built.
    <div className="overflow-x-auto">
      <div className="min-w-[1200px]">
        <ResponsiveGridLayout
          className="dashboard-grid"
          layouts={layouts}
          // Single breakpoint at 0 — never switches, so never reflows.
          breakpoints={{ lg: 0 }}
          cols={{ lg: 12 }}
          rowHeight={40}
          isDraggable={isEditing}
          isResizable={isEditing}
          draggableHandle=".widget-drag-handle"
          onLayoutChange={handleLayoutChange}
          margin={[16, 16] as [number, number]}
        >
          {visibleWidgets.map((widget) => {
            const Component = WIDGET_COMPONENTS[widget.id];
            if (!Component) return null;

            return (
              <div key={widget.id} className="relative">
                <div className="group relative h-full">
                  <div className="absolute left-0 top-0 h-3 w-3 border-l-2 border-t-2 border-amber" />
                  <div className="absolute right-0 top-0 h-3 w-3 border-r-2 border-t-2 border-amber" />
                  <div className="absolute bottom-0 left-0 h-3 w-3 border-b-2 border-l-2 border-amber" />
                  <div className="absolute bottom-0 right-0 h-3 w-3 border-b-2 border-r-2 border-amber" />

                  <div
                    className={`h-full overflow-hidden border bg-panel-dark p-4 transition-all duration-300 ${
                      isEditing
                        ? 'border-amber/40 hover:border-amber/80'
                        : 'border-panel-border hover:border-amber/30'
                    }`}
                  >
                    <div className="pointer-events-none absolute inset-0 shadow-[inset_0_2px_8px_rgba(0,0,0,0.6)]" />

                    {isEditing && (
                      <div className="widget-drag-handle absolute left-0 right-0 top-0 z-10 flex h-7 cursor-grab items-center justify-center border-b border-amber/20 bg-amber/10 active:cursor-grabbing">
                        <GripVertical className="h-4 w-4 text-amber/60" />
                        <span className="ml-1 font-mono text-[9px] uppercase text-amber/60">
                          {WIDGET_TITLES[widget.id]}
                        </span>
                      </div>
                    )}

                    <div className={`relative h-full ${isEditing ? 'pt-7' : ''}`}>
                      <Component />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </ResponsiveGridLayout>
      </div>
    </div>
  );
};

export default DashboardGrid;
